import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Clock, CheckCircle2, Plus, RefreshCw, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { mget, mpost, errMsg, fmtDay } from "@/manager/lib";
import { PageHeader, StatCard, Card, KindPill, StatusPill, Empty, goldBtn } from "@/manager/ui";
import EventFormDialog from "@/manager/components/EventFormDialog";

const LEVEL = {
  urgent: { icon: AlertTriangle, cls: "border-l-rose-500 bg-rose-50/50", tag: "Urgente", tagCls: "text-rose-600" },
  pending: { icon: Clock, cls: "border-l-amber-400 bg-white", tag: "Pendiente", tagCls: "text-amber-700" },
};

function PriorityList({ items }) {
  const navigate = useNavigate();
  if (!items.length) return <Empty>Nada pendiente. Todo bajo control.</Empty>;
  return (
    <div className="space-y-2 max-h-[560px] overflow-y-auto pr-1" data-testid="dashboard-priorities">
      {items.map((p, i) => {
        const L = LEVEL[p.level];
        return (
          <button key={i} onClick={() => navigate(p.link)} data-testid={`priority-item-${i}`}
            className={`w-full text-left border border-[#e6e1d6] border-l-4 rounded-xl px-4 py-3 flex items-center gap-3 hover:shadow-sm transition-shadow ${L.cls}`}>
            <L.icon size={17} className={L.tagCls} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-[#101B28] truncate">{p.title}</p>
              <p className="text-xs text-[#6b6a66] truncate">{p.subtitle}</p>
            </div>
            <span className={`text-[10px] uppercase tracking-[0.16em] ${L.tagCls}`}>{L.tag}</span>
            <ArrowRight size={14} className="text-[#6b6a66]" />
          </button>
        );
      })}
    </div>
  );
}

function WeekList({ events }) {
  const navigate = useNavigate();
  if (!events.length) return <Empty>No hay eventos en los próximos 7 días.</Empty>;
  return (
    <div className="divide-y divide-[#eee9df]" data-testid="dashboard-week">
      {events.map((e) => (
        <button key={e.id} onClick={() => navigate(`/manager/eventos/${e.id}`)} className="w-full text-left py-3 flex items-center gap-3 hover:bg-[#F7F5F0] px-2 rounded-lg" data-testid={`week-event-${e.id}`}>
          <div className="w-16 text-xs uppercase text-[#6b6a66]">{fmtDay(e.date)}</div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{e.title}</p>
            <p className="text-xs text-[#6b6a66]">{e.start_time || "--:--"} · {e.guests ?? "?"} pers. · camareros {e.staff.confirmed}/{e.staff.needed}</p>
          </div>
          <KindPill kind={e.kind} /><StatusPill status={e.status} />
        </button>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [newEvent, setNewEvent] = useState(false);
  const navigate = useNavigate();
  const load = useCallback(() => mget("/dashboard").then(setD).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const sync = async () => { const r = await mpost("/sync"); toast.success(`Sincronizado con la web: ${r.created} nuevos, ${r.updated} actualizados, ${r.leads} solicitudes`); load(); };
  if (!d) return <p className="text-sm text-[#6b6a66]">Cargando…</p>;
  const s = d.stats;
  return (
    <div data-testid="manager-dashboard">
      <PageHeader eyebrow="Panel de control" title="Todo Aura, bajo control.">
        <Button variant="outline" onClick={sync} className="rounded-full gap-2" data-testid="dashboard-sync"><RefreshCw size={14} /> Sincronizar web</Button>
        <Button onClick={() => setNewEvent(true)} className={goldBtn} data-testid="dashboard-new-event"><Plus size={15} /> Evento</Button>
        <Button variant="outline" onClick={() => navigate("/manager/tareas?new=1")} className="rounded-full" data-testid="dashboard-new-task">+ Tarea</Button>
        <Button variant="outline" onClick={() => navigate("/manager/crm?new=1")} className="rounded-full" data-testid="dashboard-new-lead">+ Interesado</Button>
        <Button variant="outline" onClick={() => navigate("/manager/inventario?move=1")} className="rounded-full" data-testid="dashboard-new-movement">+ Movimiento stock</Button>
      </PageHeader>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
        <StatCard label="Eventos hoy" value={s.today} onClick={() => navigate("/manager/agenda")} testid="stat-today" />
        <StatCard label="Próximos 7 días" value={s.week} onClick={() => navigate("/manager/agenda")} testid="stat-week" />
        <StatCard label="Eventos del mes" value={s.month} onClick={() => navigate("/manager/eventos")} testid="stat-month" />
        <StatCard label="Solicitudes nuevas" value={s.new_leads} tone={s.new_leads ? "warn" : "default"} onClick={() => navigate("/manager/crm")} testid="stat-leads" />
        <StatCard label="Tareas abiertas" value={s.open_tasks} onClick={() => navigate("/manager/tareas")} testid="stat-tasks" />
        <StatCard label="Tareas vencidas" value={s.overdue} tone={s.overdue ? "alert" : "default"} onClick={() => navigate("/manager/tareas")} testid="stat-overdue" />
        <StatCard label="Camareros por confirmar" value={s.staff_missing} tone={s.staff_missing ? "alert" : "default"} onClick={() => navigate("/manager/personal")} testid="stat-staff" />
        <StatCard label="Limpiezas pendientes" value={s.cleaning} tone={s.cleaning ? "warn" : "default"} onClick={() => navigate("/manager/limpieza")} testid="stat-cleaning" />
        <StatCard label="Alertas de stock" value={s.stock_alerts} tone={s.stock_alerts ? "alert" : "default"} onClick={() => navigate("/manager/compras")} testid="stat-stock" />
        <StatCard label="Cobros pendientes" value={s.pending_payments} tone={s.pending_payments ? "warn" : "default"} onClick={() => navigate("/manager/finanzas")} testid="stat-payments" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <Card className="xl:col-span-3 p-5">
          <h2 className="font-display text-xl mb-4">Centro de prioridades</h2>
          <PriorityList items={d.priorities} />
        </Card>
        <div className="xl:col-span-2 space-y-6">
          <Card className="p-5">
            <h2 className="font-display text-xl mb-2">Esta semana</h2>
            <WeekList events={d.week} />
          </Card>
          <Card className="p-5" data-testid="dashboard-ready">
            <h2 className="font-display text-xl mb-3 flex items-center gap-2"><CheckCircle2 size={18} className="text-emerald-600" /> Todo preparado</h2>
            {d.ready.length ? d.ready.map((e) => (
              <button key={e.id} onClick={() => navigate(`/manager/eventos/${e.id}`)} className="block text-sm text-emerald-800 py-1 hover:underline">{fmtDay(e.date)} · {e.title}</button>
            )) : <p className="text-sm text-[#6b6a66]">Aún no hay eventos con todo listo esta semana.</p>}
          </Card>
        </div>
      </div>
      <EventFormDialog open={newEvent} onClose={() => setNewEvent(false)} onSaved={(ev) => navigate(`/manager/eventos/${ev.id}`)} />
    </div>
  );
}
